import { ReactNode } from 'react';
import { RouteComponentProps } from 'react-router';
import { DateTime } from 'luxon';
import { SentEmailPreview } from '../../shared/components/email-preview-modal/type';
import MasterLog from '../../shared/enums/master-log-enum';
import {
  CategoryUpdatedType,
  UniboxActionType,
  UnifiedInboxFeed,
} from './enum/unified-inbox.enum';
import { ProspectOutcomeDisplayName } from '../sequence/enums';
import { OutcomeSentiment } from '../settings/enums/custom-outcome';
import { ConversationType } from '../prospect/enums/prospect';

export type UnifiedInboxProps = RouteComponentProps;

export type EmailInboxFilters = {
  page: number;
  limit: number;

  feed?: UnifiedInboxFeed;
  search: string;
  conversationType: ConversationType;

  clients: { id: number; email: string }[];
  sequences: { id: number; title: string }[];
  owners: { id: number; firstName: string }[];
  emailAccounts: { id: number; email: string }[];
  outcomes: { id: number; name: string }[];
  date: { date: DateTime | string; displayDate: string }[];
  sentiment?: string;
  subSequenceIds?: { id: number; name: string }[];
  subSequenceStepIds?: number[];
};

export type EmailInboxFiltersPayload = {
  page: number;
  limit: number;

  search: string;

  clientIds: number[];
  sequenceIds: number[];
  owners: number[];
  emailAccountIds: number[];
  categoryId: number;
  categoryIds: number[];
  date: { startDate: DateTime | string; endDate: DateTime | string };
  isRead?: boolean;
  status?: string;
  type?: ConversationType;
  sentiment?: string;
  subSequenceIds?: number[];
};

export enum UnifiedEntityType {
  PROSPECT = 'prospect',
  DOMAIN = 'domain',
}

export type EmailListItem = {
  emailId: string;
  emailThreadId: string;
  hashId: string;
  fromName: string;
  prospectName: string;
  subject: string;
  content: string;
  sentAt: string;
  isRead: number;
  isRepliedByProspect: boolean;
  sequenceName: string;
  sequenceId: string;
  categoryId: number;
  isSelected: boolean;
  isFinished: boolean;
  isUnsubscribed: boolean;
  isSubscribed: boolean;
  categoryUpdatedBy: CategoryUpdatedType;
  status?: EmailScheduleStatus;
  scheduledAt?: string;
  scheduleContent?: string;
  timezone?: string;
  failedReason?: string;
  entity?: string;
  entityType?: UnifiedEntityType;
  isEmailAccountDeleted: boolean;
};

export type EmailListMeta = {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
};

export type EmailThreadFilters = {
  hashId: string;
};

export enum EmailScheduleStatus {
  Draft = 'draft',
  Scheduled = 'scheduled',
  InQueue = 'inQueue',
  Failed = 'failed',
}

export type UnifiedScheduleAttachment = {
  id: number;
  type: string;
  size: string;
  name: string;
};

export type UnifiedSchedule = {
  fromName: string;
  fromEmail: string;
  unifiedScheduledId: number;
  repliedOnEmailId: number;
  emailThreadId: number;
  content: string;
  scheduledAt?: string; // ISO String
  status: EmailScheduleStatus;
  timezone?: string;
  sendEmailIfReplied: boolean;
  failedReason?: string;
  to: string[];
  cc: string[];
  bcc: string[];
  attachments: UnifiedScheduleAttachment[];
  templateId?: number;
  createdAt?: string;
};

export type EmailThreadItemSubseqeunceType = {
  sequenceId: number;
  sequenceName: string;
  parentSequenceId: number;
};

export type EmailThreadItemSeqeunceType = {
  sequenceId: number;
  sequenceName: string;
};

export type EmailThreadItem = {
  emailId: number;
  subject: string;
  preheader: string;
  content: string;
  sentAt: string;
  clickCount: number;
  lastClickedAt: string;
  openCount: number;
  lastOpenedAt: string;
  to: string[];
  fromEmail: string;
  fromName: string;
  isRepliedByProspect: boolean;
  cc: string[];
  bcc: string[];
  toProspectId: number;
  fromProspectId: number;
  attachments: [];
  failedAttachments: [];
  unifiedScheduled?: UnifiedSchedule;
  subSequences?: EmailThreadItemSubseqeunceType[];
  sequenceId?: number;
  sequences?: EmailThreadItemSeqeunceType[];
  isEmailAccountDeleted?: boolean;
};

export type EmailThreadContentPayload = {
  emailThreadId: string;
  emailId: number;
};

export type EmailThreadActivityPayload = {
  id: number;
  type: MasterLog[];
};

export type ReplyEmailPayload = {
  emailThreadId: number;
  emailId: number;
  content: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  templateId?: number;
  attachmentIds?: number[];
  unifiedScheduledId?: number;
};

export type ForwardEmailPayload = {
  emailThreadId: number;
  emailId: number;
  content: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  templateId?: number;
  attachmentIds?: number[];
};

export type AttachmentUploadUniboxPayload = {
  emailId?: number;
  formData: FormData;
  onUploadProgress: any;
  cancelToken: any;
};

export type AttachmentDeleteUniboxPayload = {
  emailId?: number;
  attachmentId: number;
  deleteFromTemplate: boolean;
  sequenceStepVariantId?: number;
};

export type AttachmentUploadResponse = {
  attachmentId: number;
  attachmentIdentifier: string;
};

export type ReplyForwardEditorAction = 'forward' | 'reply' | 'replyAll';

export type ReplyForwardEmailPreview = SentEmailPreview & {
  emailId?: number;
  emailThreadId?: string;
  toProspectId: number;
  fromProspectId: number;
};

export type UnifiedInboxEmailThreadActivity = {
  id?: string;
  key: MasterLog;
  timestamp: string;
  log: string;
};

export interface UnifiedInboxRouteParams {
  hashId?: string;
}
export interface UnifiedInboxRouteState {
  emailThreadId: string;
}

export type EmailCategoryType = {
  id: number;
  name: ProspectOutcomeDisplayName | string;
  emoji: string | ReactNode;
  totalEmailsInCategory?: number;
  textColor: string;
  bgColor: string;
  isDefault?: boolean;
  sentiment?: OutcomeSentiment;
};

export type EmailAccountsType = {
  id: number;
  fromEmail: string;
  isDeleted: boolean;
};

export type EmailCategoryOption = EmailCategoryType & { key: string };

export type EmailCategoryPayload = {
  emailThreadIds: string[];
  categoryId: number;
};

export type EmailReadUnreadStatusPayload = {
  emailThreadIds: string[];
  readStatus: boolean;
};

export type EmailDeletePayload = {
  emailThreadIds: string[];
  blockSenderEmail?: boolean;
};

export type EmailActionsPayload = {
  action: UniboxActionType;
  emailThreadIds: string[];
  categoryId?: number;
  readStatus?: boolean;
  blockSenderEmail?: boolean;
};

export type EmailBulkActionsPayload = {
  action: UniboxActionType;
  categoryId?: number;
  readStatus?: boolean;
  deSelectedEmailThreadIds: string[];
  emailsFilter: EmailInboxFiltersPayload;
};

export type IsEmailSelectedPayload = {
  select: 'single' | 'all' | 'bulk';
  hashId?: string;
  isSelected: boolean;
  filters?: EmailInboxFilters;
};

export type SelectAllEmailThreads = {
  isAllEmailThreadsSelected?: boolean;
  emailsFilter?: EmailInboxFilters;
  deSelectedEmailThreadIds?: any[];
  selectedEmailsCount?: number;
};

export type BulkMarkAsReadUnreadPayload = {
  readStatus: boolean;
  deSelectedEmailThreadIds: string[];
  emailsFilter: EmailInboxFiltersPayload;
};

export type BulkDeletePayload = {
  deSelectedEmailThreadIds: string[];
  emailsFilter: EmailInboxFiltersPayload;
};

export type BulkCategoryAssignPayload = {
  categoryId: number;
  deSelectedEmailThreadIds: string[];
  emailsFilter: EmailInboxFiltersPayload;
};

export type ReplyDraftPayload = {
  unifiedScheduledId?: number;
  emailThreadId?: number;
  repliedOnEmailId?: number;
  content: string;
  to?: string[];
  cc?: string[];
  bcc?: string[];
  attachments?: UnifiedScheduleAttachment[];
  templateId?: number;
  isFirstEmailDraft?: boolean;
  prospectId?: number;
  subject?: string;
  emailAccountId?: number;
};

export type ReplyScheduleBasePayload = {
  unifiedScheduledId: number;
  scheduledAt: string; // ISO String
  timezone: string;
  sendEmailIfReplied: boolean;
};

export type ReplySchedulePayload = ReplyScheduleBasePayload & {
  repliedOnEmailId: number;
};

export type UpdateUniboxEmailScheduleDataPayload = {
  hashId: string;
  isDraftTab: boolean;
};

export type ReplyDraftDiscardBasePayload = {
  repliedOnEmailId: number;
};

export type ReplyDraftDiscardPayload = ReplyDraftDiscardBasePayload &
  UpdateUniboxEmailScheduleDataPayload;

export type ReplyScheduleCancelBasePayload = {
  unifiedScheduledId: number;
  repliedOnEmailId: number;
};

export type ReplyScheduleCancelPayload = ReplyScheduleCancelBasePayload &
  UpdateUniboxEmailScheduleDataPayload;

export enum DraftStatus {
  Idle = 'idle', // Idle is used to track switching from Idle to Ready
  Ready = 'ready', // Ready status will trigger immediate draft saving API invocation
}

export type ExclusionListBlockPayload = {
  emailThreadId: string;
  type: ExclusionListEntityType.Email | ExclusionListEntityType.Domain;
  deleteOnBlock: boolean;
};

export enum ExclusionListEntityType {
  Email = 'email',
  Domain = 'domain',
}
export enum EmailStatus {
  Draft = 'draft',
}

export type UnifiedInboxSubsequenceFilter = {
  id: number;
  title: string;
  subsequences: MinimalSubsequence[];
};

type MinimalSubsequence = {
  id: number;
  title: string;
  steps: MinimalSubsequenceStep[];
};

type MinimalSubsequenceStep = {
  id: number;
  number: number;
};

export type ProspectEmailsTabListItem = {
  emailId: number;
  emailThreadId: number;
  subject: string;
  content: string;
  to: string[];
  cc: string[];
  bcc: string[];
  sentAt: string;
  isDraft: boolean;
  fromName: string;
  fromEmail: string;
  prospectName: string | null;
  isUnsubscribed: number;
  unifiedScheduled: UnifiedSchedule;
  hashId: string;
  userId: number;
  attachments?: [];
  fromProspectId: number;
  // Optional sequence identifier for emails that belong to a sequence
  sequenceId?: number;
  repliedOnEmailId?: number;
  unifiedScheduledId?: number;
  lastOpenedAt?: string;
  lastClickedAt?: string;
  sequenceName: string;
  toProspectId: number;
  // this prop is used to identify reply email draft and only for frontend purpose
  repliedEmail?: boolean;
  isEmailAccountDeleted?: boolean;
};

export type GetProspectEmailsTabList = {
  items: ProspectEmailsTabListItem[];
  meta: {
    currentPage: number;
    itemsPerPage: number;
    totalItems: number;
    totalPages: number;
  };
};

export type SendEmailProspectTabProps = {
  emailThreadId: number;
  emailId: number;
  content: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  templateId?: number;
  attachmentIds?: number[];
  unifiedScheduledId?: number;
  subject?: string;
};

export type DeleteProspectEmailPayload = {
  emailId: number;
  shouldThreadDelete?: boolean;
};

export type EmailDraftDataProps = {
  unifiedScheduledId: number;
  emailThreadId: number;
  repliedOnEmailId: number;
  content: string;
  status: EmailStatus;
  subject: string;
  to: string[];
  cc: string[];
  bcc: string[];
  scheduledAt: null;
  timezone: null;
  sendEmailIfReplied: false;
  templateId: null;
  attachments?: UnifiedScheduleAttachment[];
};
